import {
  User,
  ClassificationGroup,
  Classification,
  ClassificationLevel,
  UserStatus,
} from '@core/generated/gql-types';
import {
  createFieldFilterConfig,
  type FilterConfig,
} from '@/components/filters';
import { formatUserDisplayName } from '@/utils/utils';

const ASSIGNED_ADMIN_FILTER_UNASSIGNED = 'Unassigned';

export type WorkerFilterConfig = FilterConfig<User>;

export type ClassificationStoreParams = {
  classificationGroups: ClassificationGroup[];
  getClassificationById: (id: string) => Classification | null;
  getClassificationLevelById: (id: string) => ClassificationLevel | null;
};

export type TenantUserFilterParams = {
  tenantUsers: User[];
  getTenantUserById: (id: string) => User | null;
};

export const getWorkerIndustryNames = (
  worker: User,
  {
    classificationGroups,
    getClassificationById,
    getClassificationLevelById,
  }: ClassificationStoreParams,
): string[] => {
  const classificationLevelIds = worker.classificationLevelIds || [];
  const industryNames = new Set<string>();
  for (const levelId of classificationLevelIds) {
    const level = getClassificationLevelById(levelId);
    const classificationId = level?.classificationId;
    if (!classificationId) continue;
    const classification = getClassificationById(classificationId);
    const groupId = classification?.classificationGroupId;
    if (!groupId) continue;
    const groupName = classificationGroups.find((g) => g.id === groupId)?.name;
    if (groupName) {
      industryNames.add(groupName);
    }
  }
  return Array.from(industryNames);
};

export const getWorkerIndustry = (
  worker: User,
  params: ClassificationStoreParams,
): string => {
  const industries = getWorkerIndustryNames(worker, params);
  return industries[0] ?? '-';
};

export const getOnboardingPercentageFromWorker = (worker: User): string => {
  const percentage = Number(worker.onboardingPercentage);
  return percentage ? Math.round(percentage).toString() : '-';
};

export const getWorkerSkillNames = (
  worker: User,
  {
    getClassificationById,
    getClassificationLevelById,
  }: Pick<
    ClassificationStoreParams,
    'getClassificationById' | 'getClassificationLevelById'
  >,
): string[] => {
  const classificationLevelIds = worker.classificationLevelIds || [];
  const classificationNames = new Set<string>();
  for (const levelId of classificationLevelIds) {
    const level = getClassificationLevelById(levelId);
    const classificationId = level?.classificationId;
    if (!classificationId) continue;
    const classificationName = getClassificationById(classificationId)?.name;
    if (classificationName) {
      classificationNames.add(classificationName);
    }
  }
  return Array.from(classificationNames);
};

const FIELD_FILTER_CONFIG = [
  createFieldFilterConfig<User>('status', 'Status', 'status'),
  createFieldFilterConfig<User>('state', 'State', 'address.state'),
  createFieldFilterConfig<User>('suburb', 'Suburb', 'address.suburb'),
  createFieldFilterConfig<User>('postcode', 'Postcode', 'address.postcode'),
];

export const createWorkerFilterConfig = (
  classificationParams: ClassificationStoreParams,
  tenantUserParams?: TenantUserFilterParams,
): WorkerFilterConfig[] => {
  const {
    classificationGroups,
    getClassificationById,
    getClassificationLevelById,
  } = classificationParams;

  const computedFilters: WorkerFilterConfig[] = [
    {
      key: 'industry',
      title: 'Industry',
      getOptions: () =>
        (classificationGroups
          ?.map((g) => g.name)
          .filter(Boolean) as string[]) ?? [],
      getValueFromItem: (worker) =>
        getWorkerIndustryNames(worker, classificationParams),
    },
    {
      key: 'skillSet',
      title: 'Skill Set',
      getOptions: () =>
        Array.from(
          new Set(
            (classificationGroups ?? []).flatMap((group) =>
              (group.classifications ?? [])
                .map((classification) => classification.name)
                .filter((name): name is string => Boolean(name)),
            ),
          ),
        ).sort(),
      getValueFromItem: (worker) =>
        getWorkerSkillNames(worker, {
          getClassificationById,
          getClassificationLevelById,
        }),
    },
    {
      key: 'onboardingPercentage',
      title: 'Onboarding %',
      getOptions: () => ['In progress', 'Completed', 'Not started'],
      getValueFromItem: (worker) => {
        const percentageStr = getOnboardingPercentageFromWorker(worker);
        if (percentageStr === '-') return ['Not started'];
        const num = Number(percentageStr);
        if (num === 0) return ['Not started'];
        if (num === 100) return ['Completed'];
        if (num > 0 && num < 100) return ['In progress'];
        return [];
      },
    },
    {
      key: 'assignedAdmin',
      title: 'Assigned admin',
      getOptions: (data: User[]) => {
        const { tenantUsers, getTenantUserById } = tenantUserParams ?? {};
        const labels = new Set<string>([ASSIGNED_ADMIN_FILTER_UNASSIGNED]);

        const activeTenantUsers =
          tenantUsers?.filter((u) => u.status === UserStatus.Active) ?? [];

        // Add assigned admin labels for all tenant users.
        for (const u of activeTenantUsers) {
          labels.add(formatUserDisplayName(u));
        }

        // Add unknown admin labels for workers that don't have a valid assigned admin.
        for (const w of data) {
          const id = w.assignedAdminId;
          if (!id || getTenantUserById?.(id)) continue;
          labels.add(`Unknown admin (${id})`);
        }

        // Return the labels in alphabetical order.
        return Array.from(labels).sort((a, b) => {
          if (a === ASSIGNED_ADMIN_FILTER_UNASSIGNED) return -1;
          if (b === ASSIGNED_ADMIN_FILTER_UNASSIGNED) return 1;
          return a.localeCompare(b);
        });
      },
      getValueFromItem: (worker: User) => {
        const { getTenantUserById } = tenantUserParams ?? {};
        const id = worker.assignedAdminId;
        if (!id) return [ASSIGNED_ADMIN_FILTER_UNASSIGNED];
        const u = getTenantUserById?.(id);
        if (u) return [formatUserDisplayName(u)];
        return [`Unknown admin (${id})`];
      },
    },
  ];

  return [...FIELD_FILTER_CONFIG, ...computedFilters];
};

export const WORKER_FILTER_KEYS = [
  ...FIELD_FILTER_CONFIG.map((f) => f.key),
  'industry',
  'skillSet',
  'onboardingPercentage',
  'assignedAdmin',
];
